const DB_NAME = 'TomSmartPetCache';
const DB_VERSION = 1;
const STORE_NAME = 'apiCache';
const DEFAULT_CACHE_TIME = 24 * 60 * 60 * 1000; // 24 小时

interface CacheItem {
  key: string;
  data: any;
  timestamp: number;
  expiresAt: number;
}

class CacheManager {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    if (this.db) return Promise.resolve();

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => { this.db = request.result; resolve(); };
      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
        }
      };
    });
    return this.initPromise;
  }

  generateKey(url: string, params?: any): string {
    const paramStr = params ? JSON.stringify(params) : '';
    return `${url}::${paramStr}`;
  }

  /** 执行一次 IndexedDB 事务：封装 init + Promise + 错误吞咽，供 set/get/delete/clear 复用 */
  private async runTx<T = undefined>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | undefined> {
    try {
      if (!this.db) await this.init();
      return await new Promise<T>((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], mode);
        const request = fn(transaction.objectStore(STORE_NAME));
        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => reject(request.error);
      });
    } catch {
      // 吞咽错误避免影响主流程；get 失败返回 undefined，调用方按 null 处理
      return undefined;
    }
  }

  async set(key: string, data: any, ttl: number = DEFAULT_CACHE_TIME): Promise<void> {
    const now = Date.now();
    const item: CacheItem = { key, data, timestamp: now, expiresAt: now + ttl };
    await this.runTx('readwrite', (store) => store.put(item));
  }

  async get(key: string): Promise<any | null> {
    const item = await this.runTx<CacheItem>('readonly', (store) => store.get(key));
    if (!item) return null;
    if (Date.now() > item.expiresAt) { this.delete(key); return null; }
    return item.data;
  }

  async delete(key: string): Promise<void> {
    await this.runTx('readwrite', (store) => store.delete(key));
  }

  async clear(): Promise<void> {
    await this.runTx('readwrite', (store) => store.clear());
  }

  async cleanExpired(): Promise<void> {
    try {
      if (!this.db) await this.init();
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const index = store.index('expiresAt');
        const range = IDBKeyRange.upperBound(Date.now());
        const request = index.openCursor(range);
        request.onsuccess = (event: any) => {
          const cursor = event.target.result;
          if (cursor) { cursor.delete(); cursor.continue(); } else { resolve(); }
        };
        request.onerror = () => reject(request.error);
      });
    } catch {
      // ignore
    }
  }
}

export const cacheManager = new CacheManager();
cacheManager.init().catch(console.error);
setInterval(() => { cacheManager.cleanExpired().catch(console.error); }, 60 * 60 * 1000);
